import React, { useState } from "react";

/**
 * Returns an embeddable iframe src for YouTube/Vimeo watch URLs, or null.
 */
export function getHostedVideoEmbedSrc(url) {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  try {
    const u = new URL(trimmed.startsWith("//") ? `https:${trimmed}` : trimmed);
    const host = u.hostname.replace(/^www\./i, "").toLowerCase();

    if (host === "youtu.be") {
      const id = u.pathname.replace(/^\//, "").split("/")[0];
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (host === "youtube.com" || host === "m.youtube.com") {
      if (u.pathname.startsWith("/embed/")) return trimmed;
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
      const shorts = u.pathname.match(/^\/shorts\/([^/?]+)/);
      if (shorts) return `https://www.youtube.com/embed/${shorts[1]}`;
    }
    if (host.endsWith("vimeo.com")) {
      const m = u.pathname.match(/\/(?:video\/)?(\d+)/);
      return m ? `https://player.vimeo.com/video/${m[1]}` : null;
    }
  } catch {
    return null;
  }
  return null;
}

function resolveAssetSrc(value, apiUrl, subdir = "events") {
  if (!value) return "";
  if (value.startsWith("//")) return `https:${value}`;
  if (/^https?:\/\//i.test(value)) return value;
  return `${apiUrl}/uploads/${subdir}/${value.replace(/^\/+/, "")}`;
}

/**
 * Click-to-play wrapper: shows the thumbnail with a play button overlay,
 * and swaps to the actual player on click. Used for hosted-video embeds
 * (YouTube/Vimeo) and remote .mp4 URLs when a custom thumbnail is provided.
 */
function ThumbnailPlayOverlay({ thumbnailSrc, title, mediaStyle, onPlay }) {
  return (
    <button
      type="button"
      onClick={onPlay}
      aria-label={`Play ${title || "video"}`}
      style={{
        position: "relative",
        display: "block",
        padding: 0,
        border: 0,
        background: "transparent",
        cursor: "pointer",
        width: "100%",
      }}
    >
      <img
        src={thumbnailSrc}
        alt={title || "Video thumbnail"}
        style={{ ...mediaStyle, display: "block" }}
      />
      <span
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: "rgba(0, 0, 0, 0.6)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 22,
          pointerEvents: "none",
        }}
      >
        ▶
      </span>
    </button>
  );
}

/**
 * Renders media for Series of Expert Speakers cards/details.
 * Supports uploads, direct image/video URLs, and YouTube/Vimeo links.
 * If a `thumbnail` (or `thumbnail_url`) is set on the event, it is used
 * as the poster image for native <video>, or as a click-to-play preview
 * for hosted embeds.
 */
export function EventMediaDisplay({
  event,
  apiUrl,
  mediaStyle,
  noMediaContent,
}) {
  const [playing, setPlaying] = useState(false);

  if (!event?.media) return noMediaContent ?? null;

  const thumbnailSrc =
    event.thumbnail_url ||
    (event.thumbnail ? resolveAssetSrc(event.thumbnail, apiUrl) : "");

  const embedSrc = getHostedVideoEmbedSrc(event.media);

  if (embedSrc) {
    if (thumbnailSrc && !playing) {
      return (
        <ThumbnailPlayOverlay
          thumbnailSrc={thumbnailSrc}
          title={event.event_name}
          mediaStyle={mediaStyle}
          onPlay={() => setPlaying(true)}
        />
      );
    }
    return (
      <iframe
        src={`${embedSrc}${embedSrc.includes("?") ? "&" : "?"}autoplay=${
          playing ? 1 : 0
        }`}
        title={event.event_name || "Video"}
        style={{
          ...mediaStyle,
          border: 0,
          aspectRatio: "16 / 9",
          minHeight: 160,
        }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    );
  }

  const src = resolveAssetSrc(event.media, apiUrl);
  if (event.media_type === "image") {
    return <img src={src} alt={event.event_name || ""} style={mediaStyle} />;
  }

  return (
    <video
      src={src}
      controls
      poster={thumbnailSrc || undefined}
      style={mediaStyle}
    />
  );
}
